import React, { useState, useEffect, useRef } from 'react';
import ReactCountryFlag from "react-country-flag";
import styles from './PhoneInput.module.scss';
import Cross from '@src/assets/Cross.png';
import { CountryType } from '@src/utilities/types';

type PhoneInputProps = {
    title: string;
    width: string;
    countries: CountryType[];
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    clearInput: () => void;
    hasError: boolean;
    onCountryChange?: any;
    selectedCountry: CountryType;
    errorMessage?: string;
};

const PhoneInput: React.FC<PhoneInputProps> = ({
    title,
    width,
    countries,
    value,
    onChange,
    placeholder,
    clearInput,
    hasError,
    onCountryChange,
    selectedCountry,
    errorMessage
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(selectedCountry);

    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleSelect = (country: CountryType) => {
        if (country) {
            setSelected(country);
            setIsOpen(false);
            if (onCountryChange) {
                onCountryChange(country);
            }
        }
    };

    useEffect(() => {
        if (countries && countries.length > 0 && !selected) {
            setSelected(countries[0]);
        }
    }, [countries, selected]);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value)
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.container} style={{ width }}>
            <p className={styles.title}>{title}</p>
            <div className={`${styles.phoneInputWrapper} ${hasError ? styles.error : ''}`} data-error={errorMessage}>
                <div className={styles.dropdown} ref={dropdownRef}>
                    <div
                        className={styles.dropdownHeader}
                        onClick={() => setIsOpen(!isOpen)}
                        tabIndex={0}
                    >
                        {selected && (
                            <>
                                <ReactCountryFlag countryCode={selected.alpha2} svg style={{ borderRadius: '50%', overflow: 'hidden', objectFit: 'cover'}} />
                                <span className={styles.countryCode}>{selected.phone_extension}</span>
                            </>
                        )}
                        <span className={styles.arrow}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                    {isOpen && (
                        <ul className={styles.dropdownList}>
                            {countries.map((country) => (
                                <li
                                    key={country.alpha2}
                                    className={`${styles.dropdownItem} ${country.alpha2 === selected.alpha2 ? styles.selected : ''}`}
                                    onClick={() => handleSelect(country)}
                                >
                                    <ReactCountryFlag countryCode={country.alpha2} svg style={{ borderRadius: '50%', overflow: 'hidden', objectFit: 'cover'}} />
                                    <span>{country.name}</span>
                                    <span>{country.phone_extension}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <input
                    type="number"
                    value={value}
                    onChange={handlePhoneChange}
                    placeholder={placeholder}
                    className={styles.phoneNumberInput}
                />
                <button type="button" className={styles.clearButton} onClick={clearInput}>
                    <img src={Cross} alt="Clear" width={22} height={22} />
                </button>
            </div>
        </div>
    );
};

export default PhoneInput;
